import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import {
  User, Lock, Users, Plus, Trash2, Check, CreditCard, FileText, Download, Compass
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function Settings() {
  const { user, setUser } = useAuth();
  const { t } = useLanguage();
  const isPlatformAdmin = user?.role === 'admin';
  const isCenterAdmin = user?.role === 'center_admin';
  const isSubTeacher = user?.role === 'teacher';

  return (
    <div data-testid="settings-page" style={{ height: '100%', overflow: 'auto', padding: '2rem 2.5rem', background: '#F8F9FB' }}>
      <h1 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.75rem', fontWeight: 700, color: '#1A1D20', marginBottom: '2rem' }}>
        {t('set.title')}
      </h1>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: 640 }}>
        <ProfileSection user={user} setUser={setUser} />
        <PasswordSection />
        <TourSection />
        {/* Sub-teachers never see billing — entitlement is inherited from their center admin. */}
        {isCenterAdmin && <SubscriptionSection />}
        {isCenterAdmin && <ReceiptsSection />}
        {isCenterAdmin && <TeacherManagement />}
        {isSubTeacher && <ParentInfoSection user={user} />}
      </div>
    </div>
  );
}

function ParentInfoSection({ user }) {
  return (
    <SectionCard title="Your organization" icon={Users}>
      <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#64748B', margin: 0, marginBottom: '0.5rem' }}>
        You&apos;re part of{' '}
        <strong style={{ color: '#1A1D20' }}>{user?.parent_school_name || user?.school_name || 'your team'}</strong>.
        Your subscription and access settings are managed by your center admin.
      </p>
    </SectionCard>
  );
}

function SectionCard({ title, icon: Icon, children }) {
  return (
    <div style={{ background: 'white', borderRadius: '0.875rem', border: '1px solid #E2E8F0', padding: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem' }}>
        <Icon size={18} color="#1E2140" />
        <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.05rem', fontWeight: 600, color: '#1A1D20', margin: 0 }}>{title}</h3>
      </div>
      {children}
    </div>
  );
}

/* ---- Teacher: Subscription Status ---- */
function SubscriptionSection() {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [msg, setMsg] = useState('');

  const load = () => {
    setLoading(true);
    axios.get(`${API_URL}/billing/subscription`)
      .then(r => setSub(r.data)).catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const cancelSub = async () => {
    if (!window.confirm('Cancel at the end of the current billing period? You keep access until then.')) return;
    setActing(true); setMsg('');
    try {
      await axios.post(`${API_URL}/billing/cancel-subscription`, { confirm: true });
      setMsg('Cancellation scheduled.');
      load();
    } catch (e) { setMsg(e.response?.data?.detail || 'Could not cancel.'); }
    setActing(false);
  };

  const reactivate = async () => {
    setActing(true); setMsg('');
    try {
      await axios.post(`${API_URL}/billing/reactivate-subscription`);
      setMsg('Subscription reactivated.');
      load();
    } catch (e) { setMsg(e.response?.data?.detail || 'Could not reactivate.'); }
    setActing(false);
  };

  if (loading) return null;

  const periodEnd = sub?.current_period_end ? new Date(sub.current_period_end).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : null;
  const monthlyAmount = sub?.monthly_total ?? sub?.monthly_price;

  return (
    <SectionCard title="Subscription" icon={CreditCard}>
      {sub?.has_subscription ? (
        <div data-testid="subscription-active">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <Check size={16} color={sub.status === 'past_due' ? '#D97706' : '#2F9E44'} />
            <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.9rem', fontWeight: 600, color: sub.status === 'past_due' ? '#D97706' : '#2F9E44' }}>
              {sub.status === 'past_due' ? 'Payment past due' : (sub.cancel_at_period_end ? 'Active — cancels at period end' : 'Active subscription')}
            </span>
          </div>
          <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#64748B', margin: 0 }}>
            {monthlyAmount != null && `$${Number(monthlyAmount).toFixed(2)} ${(sub.currency || 'cad').toUpperCase()}/month`}
            {sub.started_at && ` \u00b7 since ${new Date(sub.started_at).toLocaleDateString()}`}
          </p>
          {periodEnd && (
            <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', color: '#94A3B8', margin: '0.25rem 0 0' }}>
              {sub.cancel_at_period_end ? `Access ends ${periodEnd}` : `Next renewal ${periodEnd}`}
            </p>
          )}
          <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {sub.cancel_at_period_end ? (
              <button onClick={reactivate} disabled={acting} data-testid="reactivate-subscription-btn" style={btnStyle}>
                {acting ? 'Working...' : 'Reactivate'}
              </button>
            ) : (
              <button onClick={cancelSub} disabled={acting} data-testid="cancel-subscription-btn"
                style={{ ...btnStyle, background: 'white', color: '#E03131', border: '1.5px solid #E2E8F0' }}>
                {acting ? 'Working...' : 'Cancel at period end'}
              </button>
            )}
            {msg && <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#475569' }}>{msg}</span>}
          </div>
        </div>
      ) : (
        <div>
          <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#64748B', margin: '0 0 0.75rem' }}>
            No active subscription. Contact your administrator for access.
          </p>
        </div>
      )}
    </SectionCard>
  );
}




function ProfileSection({ user, setUser }) {
  const [name, setName] = useState(user?.name || '');
  const [school, setSchool] = useState(user?.school_name || '');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const save = async () => {
    setSaving(true);
    setMsg('');
    try {
      const { data } = await axios.put(`${API_URL}/auth/profile`, { name, school_name: school });
      setUser(prev => ({ ...prev, name: data.name, school_name: data.school_name }));
      setMsg('Saved');
    } catch { setMsg('Error saving'); }
    setSaving(false);
  };

  return (
    <SectionCard title="Profile" icon={User}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <InputField label="Email" value={user?.email || ''} disabled />
        <InputField label="Name" value={name} onChange={setName} testId="settings-name-input" />
        <InputField label="School" value={school} onChange={setSchool} testId="settings-school-input" />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button onClick={save} disabled={saving} data-testid="settings-save-profile"
            style={btnStyle}>{saving ? 'Saving...' : 'Save Changes'}</button>
          {msg && <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: msg === 'Saved' ? '#2F9E44' : '#E03131' }}>{msg}</span>}
        </div>
      </div>
    </SectionCard>
  );
}

function TourSection() {
  const navigate = useNavigate();
  const start = () => {
    navigate('/browse');
    // Tiny delay so the tour mounts on /browse and can find the first anchor.
    setTimeout(() => window.dispatchEvent(new CustomEvent('frenchboard:start-tour')), 120);
  };
  return (
    <SectionCard title="Product tour" icon={Compass}>
      <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#64748B', margin: 0, marginBottom: '1rem' }}>
        A quick walk-through of how to put a game on the board, preview questions, and review with the class. Takes about a minute.
      </p>
      <button onClick={start} data-testid="settings-start-tour-btn" style={btnStyle}>
        <Compass size={16} /> Take the tour
      </button>
    </SectionCard>
  );
}

function PasswordSection() {
  const [curr, setCurr] = useState('');
  const [newPw, setNewPw] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (newPw.length < 6) { setMsg('Min 6 characters'); return; }
    setSaving(true); setMsg('');
    try {
      await axios.put(`${API_URL}/auth/password`, { current_password: curr, new_password: newPw });
      setMsg('Password updated');
      setCurr(''); setNewPw('');
    } catch (e) { setMsg(e.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  return (
    <SectionCard title="Change Password" icon={Lock}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <InputField label="Current Password" type="password" value={curr} onChange={setCurr} testId="settings-current-pw" />
        <InputField label="New Password" type="password" value={newPw} onChange={setNewPw} testId="settings-new-pw" />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button onClick={save} disabled={saving} data-testid="settings-change-password"
            style={btnStyle}>{saving ? 'Updating...' : 'Update Password'}</button>
          {msg && <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: msg.includes('updated') ? '#2F9E44' : '#E03131' }}>{msg}</span>}
        </div>
      </div>
    </SectionCard>
  );
}

function TeacherManagement() {
  const [teachers, setTeachers] = useState([]);
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [tName, setTName] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => { loadTeachers(); }, []);
  const loadTeachers = async () => {
    try { const { data } = await axios.get(`${API_URL}/center/teachers`); setTeachers(data); } catch { /* noop */ }
  };

  const create = async () => {
    if (!email || !pw || !tName) { setMsg('Fill all fields'); return; }
    setMsg('');
    try {
      await axios.post(`${API_URL}/center/teachers`, { email, password: pw, name: tName });
      setEmail(''); setPw(''); setTName('');
      setMsg('Created');
      loadTeachers();
    } catch (e) { setMsg(e.response?.data?.detail || 'Error'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this teacher and all their sessions / playlists / classrooms?')) return;
    try { await axios.delete(`${API_URL}/center/teachers/${id}`); loadTeachers(); } catch { /* noop */ }
  };

  return (
    <SectionCard title="My Team" icon={Users}>
      <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#64748B', margin: 0, marginBottom: '1rem' }}>
        Add teachers to your organization. They share your subscription and Programs access; they cannot see billing.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
        <InputField label="Teacher Name" value={tName} onChange={setTName} testId="settings-teacher-name" />
        <InputField label="Email" value={email} onChange={setEmail} testId="settings-teacher-email" />
        <InputField label="Password" value={pw} onChange={setPw} testId="settings-teacher-pw" />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem' }}>
        <button onClick={create} data-testid="settings-create-teacher" style={btnStyle}>
          <Plus size={16} /> Add Teacher
        </button>
        {msg && <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: msg === 'Created' ? '#2F9E44' : '#E03131' }}>{msg}</span>}
      </div>
      {teachers.length > 0 && (
        <div style={{ borderTop: '1px solid #F1F5F9', paddingTop: '1rem' }}>
          {teachers.map(t => (
            <div key={t._id} data-testid={`teacher-row-${t._id}`} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '0.625rem 0', borderBottom: '1px solid #F8F9FB',
            }}>
              <div>
                <span style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 600, color: '#1A1D20', fontSize: '0.9rem' }}>{t.name}</span>
                <span style={{ fontFamily: 'Nunito, sans-serif', color: '#94A3B8', fontSize: '0.8rem', marginLeft: '0.75rem' }}>{t.email}</span>
                <span style={{ fontFamily: 'Nunito, sans-serif', color: '#94A3B8', fontSize: '0.75rem', marginLeft: '0.75rem' }}>
                  {t.total_sessions} session{t.total_sessions !== 1 ? 's' : ''}
                </span>
              </div>
              <button onClick={() => remove(t._id)} data-testid={`settings-remove-teacher-${t._id}`} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#E03131', padding: '4px' }}>
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}


/* ---- Teacher: Receipts / Invoices ---- */
function ReceiptsSection() {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/billing/my-receipts`)
      .then(r => setReceipts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const downloadReceipt = async (sessionId) => {
    setDownloading(sessionId);
    try {
      const resp = await axios.get(`${API_URL}/billing/receipt/${sessionId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt_${sessionId.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {}
    setDownloading(null);
  };

  if (loading || receipts.length === 0) return null;

  return (
    <SectionCard title="Receipts" icon={FileText}>
      <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#64748B', marginBottom: '1rem' }}>
        Download tax receipts for your payments.
      </p>
      <div data-testid="receipts-list">
        {receipts.map((r, i) => (
          <div key={r.session_id} data-testid={`receipt-row-${i}`} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '0.625rem 0', borderBottom: '1px solid #F8F9FB',
          }}>
            <div>
              <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 600, color: '#1A1D20', fontSize: '0.9rem' }}>
                ${r.amount?.toFixed(2)} {(r.currency || 'cad').toUpperCase()}
              </div>
              <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.75rem', color: '#94A3B8' }}>
                {r.created_at ? new Date(r.created_at).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}
                {r.school_name ? ` \u00b7 ${r.school_name}` : ''}
              </div>
            </div>
            <button
              onClick={() => downloadReceipt(r.session_id)}
              disabled={downloading === r.session_id}
              data-testid={`download-receipt-${i}`}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.375rem',
                padding: '0.4rem 0.75rem', borderRadius: '0.5rem',
                background: 'white', border: '1.5px solid #E2E8F0',
                fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', fontWeight: 600,
                color: '#1A1D20', cursor: 'pointer',
              }}
            >
              <Download size={14} />
              {downloading === r.session_id ? 'Downloading...' : 'PDF'}
            </button>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function InputField({ label, value, onChange, disabled, type = 'text', testId }) {
  return (
    <div>
      <label style={{ display: 'block', fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', fontWeight: 600, color: '#1A1D20', marginBottom: '0.375rem' }}>
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange ? e => onChange(e.target.value) : undefined}
        disabled={disabled}
        data-testid={testId}
        style={{
          width: '100%', padding: '0.625rem 0.875rem', borderRadius: '0.5rem',
          border: '1.5px solid #E2E8F0', fontFamily: 'Nunito, sans-serif',
          fontSize: '0.9rem', outline: 'none', background: disabled ? '#F8F9FB' : 'white',
          color: disabled ? '#94A3B8' : '#1A1D20',
        }}
      />
    </div>
  );
}

const btnStyle = {
  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
  padding: '0.625rem 1.25rem', borderRadius: '0.625rem',
  background: '#1E2140', color: 'white', border: 'none',
  fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', fontWeight: 600,
  cursor: 'pointer',
};

const pillBtn = (active, muted) => ({
  padding: '0.5rem 1rem', borderRadius: '0.5rem',
  border: active ? '2px solid #1E2140' : '1.5px solid #E2E8F0',
  background: active ? '#1E2140' : 'white',
  color: active ? 'white' : (muted ? '#94A3B8' : '#64748B'),
  fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', fontWeight: 600,
  cursor: 'pointer', transition: 'all 0.15s',
});
